<script lang="ts" setup>
import { useProductCardData } from "~/composables/ProductCardData"
import type { Product } from "~/models/Product"
import { useCountDown } from "~/composables/useCountDown"
import Rating from "~/components/ShowRating.vue"
import { capitalizeFirstLetter } from "~/common/helpers"

/**
 *
 *  Props and emits section
 *
 *
 */

const props = defineProps<{
    product: Product | Combo
    withoutLabels?: boolean
    isHome?: boolean
}>()

/**
 *
 *  Ref, computed and hooks section
 *
 *
 */

const { slide, quantity, add, show } = useProductCardData(props.product)

const loading = ref(false)

const { countDownEnabled } = useCountDown(props.product)

/**
 *
 *  Life cicle events
 *
 *
 */

/**
 *
 *  Functions section
 *
 *
 */

const getImageSource = (image: any) => {
    if (image && "id" in image) {
        return image.type === "image/avif" ? image.origin : image.thumb
    } else {
        return ""
    }
}

const getImageId = (image: any) => {
    if (image && "id" in image) {
        return image.id
    } else {
        return ""
    }
}

const getImageStyle = (image: string) => {
    const imageObject = new Image()
    imageObject.src = image

    return {
        backgroundSize: "contain",
        backgroundPosition: "center center",
    }
}

const onUpdateAmount = (amount: number) => {
    quantity.value = amount
}

const onAddToCart = (amount: number) => {
    loading.value = true
    add(props.product, amount)
        .then(() => {
            loading.value = false
        })
        .catch(() => {
            loading.value = false
        })
}
const showProduct = () => show(props.product)
</script>
<template>
    <div class="tw-flex tw-flex-col tw-w-full tw-h-full card-product-container">
        <div
            class="tw-bg-white tw-cursor-pointer tw-rounded-md tw-relative tw-border tw-flex tw-flex-col tw-gap-4 tw-justify-between tw-pb-5 tw-h-full"
        >
            <WarrantyLabel
                variant="primary"
                :warranty_text="$t('products.soon')"
                v-if="props.product.soon && !props.withoutLabels"
            />

            <WarrantyLabel
                variant="secondary"
                :warranty_text="$t('products.in_offer')"
                v-else-if="
                    (product.stock?.in_stock || product.has_attribute_terms) &&
                    countDownEnabled &&
                    !props.withoutLabels
                "
            />
            <div
                class="tw-flex tw-justify-center tw-w-full tw-aspect-product-card-image tw-relative"
            >
                <q-carousel
                    class="tw-w-full tw-cursor-pointer tw-rounded-[3px] tw-h-full card-product-image-container"
                    v-model="slide"
                    infinite
                    autoplay
                    animated
                    transition-next="fade"
                    transition-prev="fade"
                    @click="showProduct"
                >
                    <q-carousel-slide
                        v-if="product.images?.length === 0"
                        name="-1"
                        img-src="/img/product_placeholder.png"
                        class="card_product_image tw-bg-cover tw-bg-center"
                    />
                    <template v-else>
                        <q-carousel-slide
                            class="card_product_image"
                            v-for="(image, i) in product.images"
                            :key="i"
                            :name="getImageId(image)"
                            :img-src="getImageSource(image)"
                            :style="getImageStyle(image.thumb)"
                        />
                    </template>
                </q-carousel>
                <div
                    class="tw-flex tw-gap-2 tw-w-5 tw-h-5 tw-absolute tw-top-2 tw-right-12 tw-items-center"
                >
                    <FavoriteHeartButton
                        v-if="product"
                        :product="product"
                        size="medium"
                        class="hover-visible tw-z-20"
                        @click.stop
                    />
                    <q-icon
                        name="visibility"
                        @click="showProduct"
                        class="tw-text-customGray-600 !tw-hidden button_card_product"
                    />
                </div>
            </div>

            <div
                :class="
                    isHome
                        ? 'countdown-home-mobile-container md:tw-mx-auto'
                        : 'tw-mx-auto'
                "
            >
                <CountDown
                    :product="product"
                    v-if="
                        (product.stock?.in_stock ||
                            product.has_attribute_terms ||
                            props.product.soon) &&
                        countDownEnabled
                    "
                />
            </div>

            <div
                class="tw-px-4 tw-relative tw-flex tw-flex-col tw-gap-4 tw-justify-between tw-h-full"
            >
                <p class="tw-text-customGray-300 tw-text-xs tw-self-start">
                    <span class="tw-font-bold">{{ product.store_name }}</span>
                    <span
                        class="tw-font-bold"
                        v-if="
                            product.categories && product.categories.length > 0
                        "
                        >/</span
                    >
                    {{ product.categories?.map((i) => i.name).join(", ") }}
                </p>

                <div class="tw-flex tw-flex-col tw-gap-3 tw-h-full">
                    <p
                        class="tw-font-semibold tw-text-start tw-my-0"
                        :class="
                            isHome
                                ? 'tw-text-md md:tw-text-xl tw-text-customGray-700 md:tw-text-primary'
                                : 'tw-text-xl tw-text-primary'
                        "
                    >
                        {{ capitalizeFirstLetter(product.name) }}
                    </p>
                    <span
                        v-if="product.is_warranty && product.warranty_formatted"
                        class="tw-text-sm tw-text-secondary"
                        >{{ product?.warranty_formatted }}</span
                    >
                    <span
                        v-if="product.brand"
                        :class="isHome ? 'tw-hidden md:tw-block' : ''"
                        class="tw-text-sm lg:tw-text-base tw-text-zinc-600 tw-font-semibold"
                    >
                        {{ product.brand }}</span
                    >
                    <Rating
                        :rating="product.rating_approved"
                        :valoration="product.reviews_approved"
                        color="secondary"
                        size="1.5rem"
                        :class="isHome ? 'tw-hidden md:tw-flex' : ''"
                    />
                    <ProductPrice :product="product" />
                </div>

                <!-- Mobile Home: Simple Add Button -->
                <div v-if="isHome" class="md:tw-hidden">
                    <button
                        v-if="
                            product.stock?.in_stock &&
                            !product.has_attribute_terms
                        "
                        @click="onAddToCart(1)"
                        :disabled="loading"
                        class="tw-w-full min-[430px]:tw-h-[44px] tw-py-1 min-[430px]:tw-py-0 tw-bg-secondary tw-text-white tw-font-semibold tw-text-base tw-rounded-full tw-flex tw-items-center tw-justify-center tw-gap-2 tw-cursor-pointer hover:tw-opacity-90 tw-transition-opacity"
                    >
                        <q-icon name="fa-solid fa-shopping-cart" size="16px" />
                        <span>Agregar</span>
                    </button>
                    <button
                        v-else
                        @click="showProduct"
                        class="tw-w-full min-[430px]:tw-h-[44px] tw-py-1 min-[430px]:tw-py-0 tw-bg-secondary tw-text-white tw-font-semibold tw-text-base tw-rounded-full tw-flex tw-items-center tw-justify-center tw-gap-2 tw-cursor-pointer hover:tw-opacity-90 tw-transition-opacity"
                    >
                        <q-icon name="fa-solid fa-shopping-cart" size="16px" />
                        <span>Agregar</span>
                    </button>
                </div>

                <!-- Desktop or Non-Home: Full CartControl -->
                <div :class="[isHome ? 'tw-hidden md:tw-block' : '']">
                    <CartControl
                        v-if="
                            product.stock?.in_stock &&
                            !product.has_attribute_terms
                        "
                        :minValue="1"
                        variant="secondary"
                        @onClickCart="onAddToCart"
                        @onUpdateAmount="onUpdateAmount"
                        :disabledColor="
                            !product.stock?.in_stock &&
                            !product.has_attribute_terms
                        "
                        :loading="loading"
                        colorLoading="primary"
                    />
                    <CartControl
                        v-else
                        variant="secondary"
                        @onClickCart="showProduct"
                        @onUpdateAmount="onUpdateAmount"
                        :minValue="1"
                        :loading="loading"
                        colorLoading="primary"
                    />
                </div>
            </div>
        </div>
    </div>
</template>
<style>
.card_product_image {
    @apply tw-bg-no-repeat tw-aspect-product-card-image tw-relative;
}

.card-product-container:hover .button_card_product {
    @apply !tw-inline hover:tw-text-primary;
    transition: all 0.2s ease;
}

.button_card_product:hover {
    transform: scale(0.95);
    transition: all 0.2s ease;
}

.card-product-container:hover .q-carousel__slides-container .q-panel {
    overflow: hidden;
}

.card-product-container:hover .card_product_image {
    transform: scale(1.1);
    transition: all 0.5s ease;
    transform-origin: center center;
}

/* Mobile Styles for Home Page - Only for screens smaller than md (768px) */
@media (max-width: 767px) {
    /* CountDown mobile styles for home page */
    .countdown-home-mobile-container :deep(.q-field__control) {
        width: 159px !important;
        height: 30px !important;
        min-height: 30px !important;
        border-radius: 3px !important;
        border: 2px solid #ffa380 !important;
        padding: 2px 6px !important;
        background: #ffffff !important;
    }

    .countdown-home-mobile-container :deep(.q-field__native) {
        padding: 0 !important;
        font-size: 12px !important;
    }

    /* CartControl button mobile styles for home page */
    .cart-control-home-mobile-container :deep(button) {
        width: 164px !important;
        height: 40px !important;
        border-radius: 50px !important;
        background: #ff5b00 !important;
        font-size: 14px !important;
        padding: 8px 16px !important;
    }

    .cart-control-home-mobile-container :deep(.q-btn) {
        width: 164px !important;
        height: 40px !important;
        min-height: 40px !important;
    }
}
</style>
